import { useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Pencil, AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { fmtNum } from "@/lib/helpers";

function overflow(branch, employees, emp, draft) {
  const out = {};
  branch.items.forEach((l) => {
    const others = employees.filter((e) => e.scope_id !== emp.scope_id).reduce((s, e) => s + Number(e.manual?.[l.item_id] || 0), 0);
    const total = others + Number(draft[l.item_id] || 0);
    if (draft[l.item_id] !== undefined && draft[l.item_id] !== "" && total > l.target_qty) out[l.item_id] = { total, others, over: total - l.target_qty };
  });
  return out;
}

function ExceedConfirm({ rows, onCancel, onConfirm }) {
  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="bg-slate-900 border-2 border-red-500/60" data-testid="manual-exceed-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-300"><AlertTriangle size={20} /> Şube hedefi aşılıyor</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300">Elle girilen hedeflerin toplamı şube hedefini geçiyor. Bu satırlarda diğer personelin otomatik payı 0 olacak.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="rounded-xl border border-red-500/30 divide-y divide-red-500/20 text-sm">
          {rows.map((r) => (
            <div key={r.item_id} className="flex justify-between gap-3 px-3 py-2"><span className="truncate">{r.name}</span>
              <span className="tabular-nums text-red-300 shrink-0">{fmtNum(r.total)} / {fmtNum(r.target_qty)} (+{fmtNum(r.over)})</span></div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-slate-700" data-testid="manual-exceed-cancel">Geri Dön</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-500" data-testid="manual-exceed-save">Yine de Kaydet</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ManualDialog({ emp, employees, branch, year, month, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => Object.fromEntries(Object.entries(emp.manual || {}).map(([k, v]) => [k, String(v)])));
  const [confirm, setConfirm] = useState(false);
  const auto = Object.fromEntries(emp.items.map((i) => [i.item_id, i.target_qty]));
  const over = overflow(branch, employees, emp, draft);
  const overRows = branch.items.filter((l) => over[l.item_id]).map((l) => ({ ...l, ...over[l.item_id] }));
  const trySave = () => (overRows.length ? setConfirm(true) : save());
  const save = async (clear = false) => {
    const item_targets = clear ? [] : Object.entries(draft).filter(([, v]) => v !== "" && v != null).map(([item_id, v]) => ({ item_id, target_qty: Number(v) }));
    try {
      await http.post("/targets", { year, month, scope: "employee", scope_id: emp.scope_id, item_targets });
      toast.success(clear ? "Otomatik paya dönüldü" : `${emp.name} hedefi kaydedildi`); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-target-dialog">
        <DialogHeader><DialogTitle>{emp.name} · Elle Hedef</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Boş bırakılan satırlar otomatik paydır: şube hedefinden elle verilenler düşülür, kalan diğer personele eşit bölünür.</p>
        <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {branch.items.filter((l) => l.target_qty > 0 || draft[l.item_id]).map((l) => (
            <div key={l.item_id} className={`flex items-center gap-3 px-3 py-2 ${over[l.item_id] ? "bg-red-500/10" : ""}`} data-testid={`manual-row-${l.item_id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm truncate">{l.name}</div>
                <div className="text-[10px] text-slate-500">Şube hedefi {fmtNum(l.target_qty)}</div>
                {over[l.item_id] && <div className="text-[10px] text-red-300 flex items-center gap-1" data-testid={`manual-over-${l.item_id}`}>
                  <AlertTriangle size={10} /> Elle toplam {fmtNum(over[l.item_id].total)} · {fmtNum(over[l.item_id].over)} adet aşım</div>}
              </div>
              <Input type="number" min="0" value={draft[l.item_id] ?? ""} placeholder={`oto ${fmtNum(auto[l.item_id] || 0)}`}
                     onChange={(e) => setDraft({ ...draft, [l.item_id]: e.target.value })}
                     className={`h-8 w-28 text-right bg-slate-950/60 ${over[l.item_id] ? "border-red-500 text-red-200" : "border-slate-700"}`} data-testid={`manual-input-${l.item_id}`} />
            </div>
          ))}
          {!branch.items.some((l) => l.target_qty > 0) && <div className="px-3 py-6 text-center text-xs text-slate-500">Önce şube hedefi girin</div>}
        </div>
        <DialogFooter className="gap-2">
          {Object.keys(emp.manual || {}).length > 0 && <Button variant="outline" onClick={() => save(true)} className="border-slate-700" data-testid="manual-target-clear">Otomatiğe Dön</Button>}
          <Button onClick={trySave} className={overRows.length ? "bg-red-600 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-500"} data-testid="manual-target-save">Kaydet</Button>
        </DialogFooter>
        {confirm && <ExceedConfirm rows={overRows} onCancel={() => setConfirm(false)} onConfirm={() => save()} />}
      </DialogContent>
    </Dialog>
  );
}

export function PersonnelTargets({ branch, employees, year, month, onSaved }) {
  const [edit, setEdit] = useState(null);
  if (!branch.personnel_targets_enabled) return null;
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass overflow-hidden" data-testid="personnel-targets">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-semibold"><UserCog size={15} className="text-blue-300" /> Personel Hedefleri</div>
      <div className="divide-y divide-slate-800">
        {employees.map((e) => {
          const manual = Object.keys(e.manual || {}).length > 0;
          return (
            <div key={e.scope_id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`personnel-row-${e.scope_id}`}>
              <span className="flex-1 font-medium truncate">{e.name}</span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${manual ? "bg-fuchsia-500/15 text-fuchsia-200" : "bg-slate-800 text-slate-400"}`}
                    data-testid={`personnel-mode-${e.scope_id}`}>{manual ? "ELLE" : "OTO"}</span>
              <span className="w-24 text-right tabular-nums" data-testid={`personnel-target-${e.scope_id}`}>{fmtNum(e.total_target)} hedef</span>
              <span className="w-20 text-right tabular-nums text-slate-400">{fmtNum(e.total_sold)} satış</span>
              <span className={`w-14 text-right font-bold ${e.total_target ? (e.hgo >= 100 ? "text-emerald-300" : "text-red-300") : "text-slate-600"}`}>{e.total_target ? `%${e.hgo}` : "—"}</span>
              <Button size="sm" variant="ghost" onClick={() => setEdit(e)} data-testid={`personnel-edit-${e.scope_id}`}><Pencil size={14} /></Button>
            </div>
          );
        })}
        {employees.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">Bu şubede hedefe dahil personel yok</div>}
      </div>
      {edit && <ManualDialog emp={edit} employees={employees} branch={branch} year={year} month={month} onClose={() => setEdit(null)} onSaved={onSaved} />}
    </Card>
  );
}
